import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getProjetosAcompanhamento } from "../api/projetos.api";
import { getTarefas } from "../api/tarefas.api";

/* ─── Helpers ─────────────────────────────────────────────────────── */

function highlight(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(96,165,250,0.3)", color: "#f1f5f9", borderRadius: 2 }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

/* ─── Componente ─────────────────────────────────────────────────── */

export default function BuscaGlobal({ open, onClose }) {
  const [query,    setQuery]    = useState("");
  const [dados,    setDados]    = useState(null); // null = não carregado
  const [loading,  setLoading]  = useState(false);
  const [selecionado, setSelecionado] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  /* Carrega dados uma vez ao abrir */
  const carregarDados = useCallback(async () => {
    if (dados) return;
    setLoading(true);
    try {
      const rows = await getProjetosAcompanhamento(false, 2);
      const projMap = {}, ticketMap = {};
      (rows || []).forEach(r => {
        if (!projMap[r.id_projeto]) {
          projMap[r.id_projeto] = { id: r.id_projeto, descricao: r.descricao, tipo: "projeto" };
        }
        if (r.ticketid && !ticketMap[r.ticketid]) {
          ticketMap[r.ticketid] = {
            id: r.ticketid,
            subject: r.subject || `#${r.ticketid}`,
            descricao_customizada: r.descricao_customizada || null,
            projeto: r.descricao,
            id_projeto: r.id_projeto,
            tipo: "ticket",
          };
        }
      });

      const ticketList = Object.values(ticketMap);
      const tarefasResults = await Promise.allSettled(ticketList.map(t => getTarefas(t.id)));
      const tarefas = tarefasResults.flatMap((r, i) => {
        if (r.status !== "fulfilled") return [];
        return r.value.map(t => ({
          ...t,
          ticketInfo: ticketList[i],
          tipo: "tarefa",
        }));
      });

      setDados({
        projetos: Object.values(projMap),
        tickets:  ticketList,
        tarefas,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dados]);

  useEffect(() => {
    if (open) {
      carregarDados();
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
      setSelecionado(0);
    }
  }, [open, carregarDados]);

  /* Resultados filtrados */
  const q = query.trim().toLowerCase();
  const resultados = q.length < 2 || !dados ? [] : [
    ...dados.projetos
      .filter(p => p.descricao?.toLowerCase().includes(q))
      .slice(0, 3)
      .map(p => ({ ...p, label: p.descricao, sub: "Projeto", cor: "#a78bfa" })),
    ...dados.tickets
      .filter(t => {
        const desc = (t.descricao_customizada || t.subject || "").toLowerCase();
        return desc.includes(q) || String(t.id).includes(q);
      })
      .slice(0, 5)
      .map(t => ({
        ...t,
        label: t.descricao_customizada || t.subject,
        sub: `#${t.id} · ${t.projeto}`,
        cor: "#60a5fa",
      })),
    ...dados.tarefas
      .filter(t => t.descritivo?.toLowerCase().includes(q))
      .slice(0, 5)
      .map(t => ({
        ...t,
        label: t.descritivo,
        sub: `${t.ticketInfo?.projeto || ""} · #${t.id_ticket}`,
        cor: "#22d3ee",
      })),
  ];

  function navegar(item) {
    let state = {};
    if (item.tipo === "projeto") {
      state = { projeto: `pai:${item.id}`, ticket: "", ts: Date.now() };
    } else if (item.tipo === "ticket") {
      state = { ticket: String(item.id), projeto: "", ts: Date.now() };
    } else if (item.tipo === "tarefa") {
      state = { ticket: String(item.id_ticket), projeto: "", ts: Date.now() };
    }
    navigate("/tarefas/acompanhamento", { state });
    onClose();
  }

  /* Navegação por teclado */
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelecionado(s => Math.min(s + 1, resultados.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelecionado(s => Math.max(s - 1, 0)); }
      if (e.key === "Enter" && resultados[selecionado]) navegar(resultados[selecionado]);
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resultados, selecionado]);

  if (!open) return null;

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>

        {/* Input */}
        <div style={inputRow}>
          <span style={{ color: "#475569", fontSize: 16 }}>🔍</span>
          <input
            ref={inputRef}
            style={searchInput}
            placeholder="Buscar projetos, tickets, tarefas..."
            value={query}
            onChange={e => { setQuery(e.target.value); setSelecionado(0); }}
          />
          {loading && <span style={{ color: "#475569", fontSize: 11 }}>Carregando...</span>}
          <kbd style={kbdStyle}>Esc</kbd>
        </div>

        {/* Dica inicial */}
        {q.length < 2 && !loading && (
          <div style={dica}>
            {dados ? "Digite pelo menos 2 caracteres para buscar" : "Carregando dados..."}
          </div>
        )}

        {/* Resultados */}
        {resultados.length > 0 && (
          <div style={lista}>
            {resultados.map((item, i) => (
              <div
                key={`${item.tipo}-${item.id || item.id_tarefa}`}
                style={resultItem(i === selecionado)}
                onMouseEnter={() => setSelecionado(i)}
                onClick={() => navegar(item)}
              >
                <span style={tipoBadge(item.cor)}>{item.tipo}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={resultLabel}>{highlight(item.label, query)}</div>
                  <div style={resultSub}>{item.sub}</div>
                </div>
                <span style={{ color: "#334155", fontSize: 11 }}>↵</span>
              </div>
            ))}
          </div>
        )}

        {q.length >= 2 && resultados.length === 0 && !loading && (
          <div style={dica}>Nenhum resultado para "{query}"</div>
        )}

        <div style={footer}>
          <span><kbd style={kbdStyle}>↑↓</kbd> navegar</span>
          <span><kbd style={kbdStyle}>↵</kbd> selecionar</span>
          <span><kbd style={kbdStyle}>Esc</kbd> fechar</span>
        </div>

      </div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const backdrop = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.7)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  paddingTop: "12vh", zIndex: 2000,
};

const box = {
  width: "100%", maxWidth: 580,
  background: "#08122a",
  border: "1px solid rgba(96,165,250,0.2)",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
};

const inputRow = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "14px 18px",
  borderBottom: "1px solid rgba(96,165,250,0.1)",
};

const searchInput = {
  flex: 1, background: "none", border: "none", outline: "none",
  color: "#f1f5f9", fontSize: 16,
};

const lista = {
  maxHeight: 360, overflowY: "auto",
};

const resultItem = sel => ({
  display: "flex", alignItems: "center", gap: 10,
  padding: "10px 18px",
  background: sel ? "rgba(96,165,250,0.1)" : "transparent",
  cursor: "pointer",
  borderBottom: "1px solid rgba(96,165,250,0.05)",
});

const tipoBadge = cor => ({
  fontSize: 9, fontWeight: 700, textTransform: "uppercase",
  color: cor, background: `${cor}18`,
  border: `1px solid ${cor}33`,
  borderRadius: 4, padding: "2px 6px",
  flexShrink: 0, letterSpacing: 0.5,
});

const resultLabel = {
  fontSize: 13, fontWeight: 500, color: "#e2e8f0",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

const resultSub = {
  fontSize: 11, color: "#475569", marginTop: 1,
};

const dica = {
  padding: "18px", color: "#334155", fontSize: 13, textAlign: "center",
};

const footer = {
  display: "flex", gap: 16, padding: "10px 18px",
  borderTop: "1px solid rgba(96,165,250,0.08)",
  color: "#334155", fontSize: 11,
};

const kbdStyle = {
  background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)",
  borderRadius: 4, padding: "1px 5px", fontSize: 10, color: "#60a5fa",
  fontFamily: "monospace",
};
